<div class="main-content">
  <div class="container-fluid">
    <div class="header-actions">
      <!--
        One market at a time for every order and cart chart: amounts are in the
        market's own currency, so markets are never added together.
      -->
      <div class="market-toggle" role="group" aria-label="Orders and sales market">
        <span class="country-filter-label">Orders &amp; sales market</span>
        @for (iso2 of markets; track iso2) {
          <label class="country-option">
            <input
              type="checkbox"
              [checked]="market === iso2"
              (change)="selectMarket(iso2, $event)"
            />
            <span class="country-swatch" [style.background]="countrySwatch(iso2, $index)"></span>
            {{ iso2 }}
          </label>
        }
        <span class="market-currency">Amounts in {{ currency }}</span>
      </div>
      <fh-refresh-button [refresh]="refreshData" />
    </div>
    <!--
      The eight country-split charts, in the order asked for: orders and sales,
      then registrations, then active users. One per row - a stacked bar with 31
      days on it has no room to share a row with another.
    -->
    @for (chart of countryCharts; track chart.key) {
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">{{ chart.title }}</h4>
              <p class="card-category">{{ chart.subtitle }}</p>
            </div>

            <!-- Order charts are one market already (see the toggle above). -->
            @if (chart.countries.length && !chart.market) {
              <div class="country-filter">
                <span class="country-filter-label">Countries</span>
                @for (iso2 of chart.countries; track iso2) {
                  <label class="country-option">
                    <input
                      type="checkbox"
                      [checked]="isCountrySelected(chart, iso2)"
                      (change)="toggleCountry(chart, iso2)"
                    />
                    <span class="country-swatch" [style.background]="countrySwatch(iso2, $index)"></span>
                    {{ iso2 }}
                  </label>
                }
                @if (chart.countries.length > 1) {
                  <button type="button" class="country-link" (click)="selectAllCountries(chart)">
                    All
                  </button>
                  <button type="button" class="country-link" (click)="clearCountries(chart)">
                    None
                  </button>
                }
              </div>
            }

            <div class="card-body" style="height: 400px;">
              <div class="chart-canvas">
                <canvas baseChart
                  [type]="barChartType"
                  [data]="chart.data"
                  [plugins]="chart.key.startsWith('daily') ? dailyScroll : monthlyScroll"
                  [options]="chart.options">
                </canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Monthly Orders by Feature</h4>
            <p class="card-category">
              Each confirmed order in the selected market attributed to the storefront feature
              that drove it; segments stack to the month's total orders. Orders placed before attribution tracking (or with
              untagged items) appear under "Others".
            </p>
          </div>
          <div class="card-body" style="height: 400px;">
            <div class="chart-canvas">
              <canvas baseChart
                [type]="barChartType"
                [data]="monthlyOrdersByFeatureData"
                [plugins]="monthlyScroll"
                [options]="stackedOrdersOptions">
              </canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Active Users by Feature (This Month)</h4>
            <p class="card-category">
              Distinct logged-in users who interacted with each feature so far this calendar
              month, most-used first. Counts usage — an order is not required.
            </p>
          </div>
          <div class="card-body" style="height: 560px;">
            <canvas baseChart
              [type]="barChartType"
              [data]="activeUsersByFeatureData"
              [options]="activeUsersByFeatureOptions">
            </canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Daily Searches</h4>
          </div>
          <div class="card-body" style="height: 400px;">
            <div class="chart-canvas">
              <canvas baseChart
                [type]="barChartType"
                [data]="dailySearchesData"
                [plugins]="dailyScroll"
                [options]="chartOptions">
              </canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Monthly Searches</h4>
          </div>
          <div class="card-body" style="height: 400px;">
            <div class="chart-canvas">
              <canvas baseChart
                [type]="barChartType"
                [data]="monthlySearchesData"
                [plugins]="monthlyScroll"
                [options]="chartOptions">
              </canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Daily Cart Activity ({{ market }})</h4>
          </div>
          <div class="card-body" style="height: 400px;">
            <div class="chart-canvas">
              <canvas baseChart
                [type]="barChartType"
                [data]="dailyCartData"
                [plugins]="dailyScroll"
                [options]="cartChartOptions">
              </canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Monthly Cart Activity ({{ market }})</h4>
          </div>
          <div class="card-body" style="height: 400px;">
            <div class="chart-canvas">
              <canvas baseChart
                [type]="barChartType"
                [data]="monthlyCartData"
                [plugins]="monthlyScroll"
                [options]="cartChartOptions">
              </canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
